import React, { useState, useRef, useEffect } from 'react';
import { progressoAPI } from '../services/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  Play, 
  CheckCircle, 
  AlertTriangle, 
  PlayCircle, 
  Trophy,
  Clock,
  Volume2
} from 'lucide-react';

function VideoPlayer({ video, cursoId, usuarioId = 'user1', onProgressUpdate }) {
  const videoRef = useRef(null);
  const progressUpdateIntervalRef = useRef(null);
  const [progresso, setProgresso] = useState({
    tempo_assistido: 0,
    concluido: false
  });
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [lastSavedProgress, setLastSavedProgress] = useState(0);

  useEffect(() => {
    if (video && video.id) {
      carregarProgresso();
    }
    
    // Limpar interval quando o componente for desmontado
    return () => {
      if (progressUpdateIntervalRef.current) {
        clearInterval(progressUpdateIntervalRef.current);
      }
    };
  }, [video]);

  const carregarProgresso = async () => {
    try {
      const response = await progressoAPI.obterProgressoVideo(video.id, usuarioId);
      const progressoData = response.data;
      setProgresso(progressoData);
      setLastSavedProgress(progressoData.tempo_assistido);
      
      // Definir tempo inicial do vídeo
      if (videoRef.current && progressoData.tempo_assistido > 0) {
        videoRef.current.currentTime = progressoData.tempo_assistido;
      }
    } catch (err) {
      console.error('Erro ao carregar progresso:', err);
    } finally {
      setCarregando(false);
    }
  };

  const atualizarProgresso = async (tempoAtual, concluido = false) => {
    try {
      // Garantir que o progresso só pode aumentar
      const novoTempo = Math.max(Math.floor(tempoAtual), progresso.tempo_assistido);
      
      const dados = {
        usuario_id: usuarioId,
        video_id: video.id,
        curso_id: cursoId,
        tempo_assistido: novoTempo,
        concluido: concluido || progresso.concluido
      };

      await progressoAPI.atualizarProgressoVideo(dados);
      
      setProgresso(prev => ({
        ...prev,
        tempo_assistido: novoTempo,
        concluido: concluido || prev.concluido
      }));
      
      setLastSavedProgress(novoTempo);
      
      // Notificar componente pai sobre atualização do progresso
      if (onProgressUpdate) {
        onProgressUpdate();
      }
    } catch (err) {
      console.error('Erro ao atualizar progresso:', err);
    }
  };

  const marcarComoConcluido = async (tempoAtual = null) => {
    try {
      const tempoFinal = tempoAtual || Math.floor(videoRef.current?.currentTime || 0);
      
      const dados = {
        usuario_id: usuarioId,
        video_id: video.id,
        curso_id: cursoId,
        tempo_assistido: Math.max(tempoFinal, progresso.tempo_assistido)
      };

      const response = await progressoAPI.marcarVideoConcluido(dados);
      setProgresso(prev => ({ ...prev, concluido: true }));
      
      // Mostrar mensagem de sucesso
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      
      // Notificar componente pai sobre atualização do progresso
      if (onProgressUpdate) {
        onProgressUpdate();
      }
      
      if (response.data.progresso_curso?.curso_concluido) {
        // Mostrar mensagem especial para conclusão do curso
        setTimeout(() => {
          setShowSuccessMessage('curso_completo');
          setTimeout(() => setShowSuccessMessage(false), 5000);
        }, 1000);
      }
    } catch (err) {
      console.error('Erro ao marcar como concluído:', err);
      setErro('Erro ao marcar vídeo como concluído');
      setTimeout(() => setErro(''), 5000);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current && !progresso.concluido) {
      const tempoAtual = videoRef.current.currentTime;
      const duracao = videoRef.current.duration;
      
      // Atualizar progresso em tempo real (apenas se aumentou)
      if (Math.floor(tempoAtual) > progresso.tempo_assistido) {
        setProgresso(prev => ({
          ...prev,
          tempo_assistido: Math.floor(tempoAtual)
        }));
      }
      
      // Marcar como concluído automaticamente se assistiu 96% do vídeo
      if (duracao && (tempoAtual / duracao) >= 0.96 && !progresso.concluido) {
        marcarComoConcluido(tempoAtual);
      }
    }
  };

  const handlePlay = () => {
    // Iniciar salvar progresso a cada 5 segundos quando o vídeo estiver tocando
    if (!progressUpdateIntervalRef.current && !progresso.concluido) {
      progressUpdateIntervalRef.current = setInterval(() => {
        if (videoRef.current && !videoRef.current.paused) {
          const tempoAtual = videoRef.current.currentTime;
          // Salvar apenas se houve progresso significativo (5 segundos ou mais)
          if (Math.floor(tempoAtual) > lastSavedProgress + 4) {
            atualizarProgresso(tempoAtual);
          }
        }
      }, 5000);
    }
  };

  const handlePause = () => {
    // Salvar progresso quando pausar
    if (videoRef.current && !progresso.concluido) {
      atualizarProgresso(videoRef.current.currentTime);
    }
    
    // Limpar interval
    if (progressUpdateIntervalRef.current) {
      clearInterval(progressUpdateIntervalRef.current);
      progressUpdateIntervalRef.current = null;
    }
  };

  const calcularPorcentagemProgresso = () => {
    if (videoRef.current && videoRef.current.duration) {
      return Math.round((progresso.tempo_assistido / videoRef.current.duration) * 100);
    }
    return 0;
  };

  const formatarDuracao = (segundos) => {
    const minutos = Math.floor(segundos / 60);
    const segs = Math.floor(segundos % 60);
    return `${minutos}:${segs.toString().padStart(2, '0')}`;
  };

  if (carregando) {
    return (
      <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-xl overflow-hidden">
        <div className="flex items-center justify-center py-32">
          <div className="text-center space-y-4">
            <div className="w-12 h-12 border-4 border-slate-700 border-t-red-600 rounded-full animate-spin mx-auto"></div>
            <p className="text-slate-300">Carregando vídeo...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!video || !video.arquivo) {
    return (
      <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-xl overflow-hidden">
        <div className="p-6">
          <div className="flex items-center gap-3 text-yellow-400 bg-yellow-400/10 border border-yellow-400/20 rounded-lg p-4">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">Vídeo não disponível</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-xl overflow-hidden">
      {/* Messages */}
      {erro && (
        <div className="p-4 m-4 bg-red-600/10 border border-red-600/20 rounded-lg flex items-center gap-3 text-red-400">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{erro}</span>
        </div>
      )}

      {showSuccessMessage && (
        <div className={`p-4 m-4 rounded-lg flex items-center gap-3 transition-all duration-300 ${
          showSuccessMessage === 'curso_completo' 
            ? 'bg-gradient-to-r from-yellow-600/10 to-orange-600/10 border border-yellow-600/20 text-yellow-400'
            : 'bg-green-600/10 border border-green-600/20 text-green-400'
        }`}>
          {showSuccessMessage === 'curso_completo' ? (
            <>
              <Trophy className="w-5 h-5 flex-shrink-0" />
              <div>
                <p className="font-semibold">🎉 Parabéns!</p>
                <p className="text-sm opacity-90">Você concluiu o curso completo!</p>
              </div>
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">✅ Vídeo marcado como concluído!</span>
            </>
          )}
        </div>
      )}
      
      {/* Header */}
      <div className="p-4 border-b border-slate-800/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center">
              <PlayCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{video.titulo}</h3>
              {video.duracao && (
                <div className="flex items-center gap-1 text-slate-400 text-sm">
                  <Clock className="w-3 h-3" />
                  <span>{formatarDuracao(video.duracao)}</span>
                </div>
              )}
            </div>
          </div>
          
          {progresso.concluido && (
            <Badge className="bg-green-600/20 text-green-400 border-green-600/30">
              <CheckCircle className="w-3 h-3 mr-1" />
              Concluído
            </Badge>
          )}
        </div>
      </div>
      
      {/* Video Player */}
      <div className="relative bg-black">
        <video
          ref={videoRef}
          controls
          className="w-full aspect-video"
          onTimeUpdate={handleTimeUpdate}
          onPlay={handlePlay}
          onPause={handlePause}
          onLoadedMetadata={() => {
            if (progresso.tempo_assistido > 0) {
              videoRef.current.currentTime = progresso.tempo_assistido;
            }
          }}
          style={{ maxHeight: '70vh' }}
        >
          <source 
            src={`http://localhost:5000/uploads/videos/${video.arquivo}`} 
            type="video/mp4" 
          />
          Seu navegador não suporta o elemento de vídeo.
        </video>
      </div>
      
      {/* Progress and Controls */}
      <div className="p-4 space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-300">
              Progresso: {calcularPorcentagemProgresso()}%
            </span>
            {videoRef.current?.duration && (
              <span className="text-slate-400">
                {formatarDuracao(progresso.tempo_assistido)} / {formatarDuracao(videoRef.current.duration)}
              </span>
            )}
          </div>
          
          <Progress 
            value={calcularPorcentagemProgresso()} 
            className="h-2" 
          />
        </div>

        {/* Action Button */}
        {!progresso.concluido && (
          <div className="flex justify-end">
            <Button
              onClick={() => marcarComoConcluido()}
              className="bg-green-600 hover:bg-green-700 text-white"
              size="sm"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Marcar como Concluído
            </Button>
          </div>
        )}

        {/* Description */}
        {video.descricao && (
          <div className="pt-4 border-t border-slate-800/50">
            <h4 className="text-sm font-semibold text-white mb-2">Descrição:</h4>
            <p className="text-slate-300 text-sm leading-relaxed">
              {video.descricao}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default VideoPlayer; 